import { Analytics, tracingCallback } from '@trello/atlassian-analytics';
// eslint-disable-next-line no-restricted-imports
import $ from '@trello/jquery';

import { isOverLimit } from 'app/scripts/lib/limits';
import type { Board } from 'app/scripts/models/board';
import { View } from 'app/scripts/views/internal/view';
import { Alerts } from 'app/scripts/views/lib/Alerts';
import { PopOver } from 'app/scripts/views/lib/PopOver';
import { ListAddTemplate } from 'app/scripts/views/templates/ListAddTemplate';
import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';

export interface InlineListComposerView {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $el: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defer: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  frameDebounce: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listenTo: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any;
}

export class InlineListComposerView extends View<void, Board> {
  className() {
    return 'js-add-list list-wrapper mod-add is-idle';
  }

  events() {
    return {
      'click .js-open-add-list': 'setOpen',
      'click .js-cancel-edit': 'setClose',
      submit: 'addList',
    };
  }

  initialize() {
    this.listenTo(
      this.model.viewState,
      'change:listComposerOpen',
      this.frameDebounce(this.renderListComposer),
    );

    this.listenTo(this.model, 'change:limits', this.render);

    this.listenTo(this.model.listList, 'add remove', this.render);

    this.handleClickOutside = this.handleClickOutside.bind(this);
    $(document).on('click', this.handleClickOutside);
  }

  remove() {
    $(document).off('click', this.handleClickOutside);
    return super.remove(...arguments);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleClickOutside(event: any) {
    // We aren't interested in this event if the list composer is not open
    if (!this.model.viewState.get('listComposerOpen')) {
      return;
    }

    // eslint-disable-next-line @trello/enforce-variable-case
    const $target = $(event.target);
    const wasClickOutside = $target.closest('.js-add-list').length === 0;
    if (wasClickOutside) {
      return this.setClose(event);
    }
  }

  render() {
    const isFirstList = this.model.listList.length === 0;

    this.$el.html(
      ListAddTemplate({
        tooManyTotalLists: isOverLimit(
          'lists',
          'totalPerBoard',
          this.model.get('limits'),
        ),
        tooManyOpenLists: isOverLimit(
          'lists',
          'openPerBoard',
          this.model.get('limits'),
        ),
        isFirstList,
      }),
    );

    this.renderListComposer();

    if (this.model.editable()) {
      this.$el.removeClass('fade hide');
    }

    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setOpen(e: any) {
    Analytics.sendClickedButtonEvent({
      buttonName: 'addAnotherListButton',
      source: 'boardScreen',
      containers: {
        board: {
          id: this.model?.id,
        },
        organization: {
          id: this.model?.getOrganization()?.id,
        },
      },
    });

    $('.js-open-add-list', this.$el).attr('tabindex', '-1');
    stopPropagationAndPreventDefault(e);
    this.model.viewState.set('listComposerOpen', true);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setClose(e: any) {
    $('.js-open-add-list', this.$el).removeAttr('tabindex');
    stopPropagationAndPreventDefault(e);
    this.model.viewState.set('listComposerOpen', false);
  }

  renderListComposer() {
    if (
      this.model.viewState.get('listComposerOpen') &&
      !isOverLimit('lists', 'totalPerBoard', this.model.get('limits')) &&
      !isOverLimit('lists', 'openPerBoard', this.model.get('limits'))
    ) {
      Analytics.sendScreenEvent({
        name: 'inlineListComposerInlineDialog',
        containers: {
          board: {
            id: this.model?.id,
          },
          organization: {
            id: this.model?.getOrganization()?.id,
          },
        },
      });
      return this.renderOpen();
    } else {
      return this.renderClose();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderOpen(e?: any) {
    stopPropagationAndPreventDefault(e);

    this.$el.removeClass('is-idle');

    this.defer(() => $('.list-name-input', this.$el).blur().focus());

    PopOver.hide();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderClose(e?: any) {
    stopPropagationAndPreventDefault(e);
    this.$el.addClass('is-idle');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createList(createListDetails: any) {
    return new Promise((resolve, reject) => {
      const traceId = Analytics.startTask({
        // @ts-expect-error TS(2322): Type '"create-list"' is not assignable to type 'Ta... Remove this comment to see the full error message
        taskName: 'create-list',
        source: 'boardScreen',
      });
      const createdList = this.model.listList.create(
        createListDetails,
        tracingCallback(
          // @ts-expect-error TS(2322): Type '"create-list"' is not assignable to type 'Ta... Remove this comment to see the full error message
          { taskName: 'create-list', source: 'boardScreen', traceId },
          (error) => {
            if (!error) {
              Analytics.sendTrackEvent({
                action: 'created',
                actionSubject: 'list',
                source: 'inlineListComposerInlineDialog',
                containers: {
                  board: {
                    id: this.model?.id,
                  },
                  organization: {
                    id: this.model?.getOrganization()?.id,
                  },
                },
              });

              // @ts-expect-error TS(2794): Expected 1 arguments, but got 0. Did you forget to... Remove this comment to see the full error message
              return resolve();
            } else {
              this.model.listList.remove(createdList);
              return reject();
            }
          },
        ),
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addList(e?: any) {
    stopPropagationAndPreventDefault(e);

    // eslint-disable-next-line @trello/enforce-variable-case
    const $input = $('.list-name-input', this.$el);
    const lists = this.model.listList;
    const listsLength = lists.length + 1;
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const listName = $input.val().trim();

    if (listName === '') {
      $input.focus().select();
      return;
    }

    this.createList({
      name: listName,
      pos: this.model.calcPos(listsLength),
      closed: false,
    })
      .then(() => {
        $input.val('');
        this.renderOpen();
        return this;
      })
      .catch(() => Alerts.flash('could not connect', 'error', 'list-composer'));
  }
}
